/**
 * Diagnostic / Inspection capture types.
 *
 * ============================================================================
 * DIAGNOSTIC CAPTURE IS NOT TIME ATTRIBUTION
 * ============================================================================
 * These types once described a "Pause-Lock System" whose purpose was to
 * SEPARATE TIME: the browser computed elapsed minutes, split them across the
 * captured jobs and locked them onto those jobs. Timer V2 8C removed that
 * entirely.
 *
 * A capture now answers ONLY: what did the technician find, how confident were
 * they, was the complaint confirmed. How much time was measured, and where it
 * goes, is decided by Timer V2's measured sessions, selected job scope, frozen
 * review and accepted allocation — never by a findings form.
 *
 * The header above said "enabling time separation", which is exactly the idea
 * that had to go.
 */

/**
 * DIAGNOSTIC COMPETENCY OUTCOME — what the diagnostic work demonstrated.
 *
 * CORE-I's own vocabulary, and deliberately NOT the disposition list below.
 * The two answer different questions and neither is inferred from the other:
 * a fault correctly diagnosed and referred out is a good diagnosis, and a
 * fault that would not present is a legitimate finding rather than a failure.
 *
 * Bound to public.diagnostic_competency_outcome_is_valid.
 */
export type DiagnosticOutcome = 'confirmed' | 'partial' | 'misdiagnosis' | 'could_not_duplicate';

/**
 * DIAGNOSTIC DISPOSITION — what happens to the job next. Timer V2's contract.
 * Bound to public.diagnostic_capture_outcome_is_valid.
 */
export type DiagnosticOutcomeExtended = 
  | 'fixed' 
  | 'needs_authorization' 
  | 'needs_parts' 
  | 'referred_out'
  | 'could_not_duplicate';

/**
 * Confidence level for diagnostic work (1-5 scale)
 */
export type DiagnosticConfidence = 1 | 2 | 3 | 4 | 5;

/**
 * Lock type distinguishes between diagnostic and inspection jobs
 * This enables differentiated analytics and training recommendations
 */
export type DiagnosticLockType = 'diagnostic' | 'inspection';

/**
 * Allocation mode stored in timer sessions
 * Includes the lock type suffix for precise tracking
 */
export type AllocationMode = 'diagnostic_lock' | 'inspection_lock' | null;

/**
 * Data captured during diagnostic/inspection pause-lock
 */
export interface DiagnosticCaptureData {
  jobId: string;
  jobName: string;
  /** What happens to the job next. Workflow disposition, not a skill verdict. */
  outcome: DiagnosticOutcomeExtended;
  /**
   * What the diagnostic work DEMONSTRATED. A separate fact, asserted by the
   * technician who did the work, and never inferred from the disposition —
   * a fault correctly diagnosed and referred out is a good diagnosis, and a
   * fault that could not be reproduced is a legitimate result rather than a
   * failure.
   */
  competencyOutcome: DiagnosticOutcome;
  confidence: DiagnosticConfidence;
  complaintConfirmed: boolean;
  lockType: DiagnosticLockType;
}

/**
 * Uncaptured diagnostic job info for the pause dialog
 */
export interface UncapturedDiagnosticJob {
  jobId: string;
  jobName: string;
  ticketJobId?: string;
  lockType: DiagnosticLockType;
}

/**
 * Pensieve `diagnostic.captured` metadata — LEGACY V1 (capture_schema_version
 * absent or 1).
 *
 * @deprecated Describes events written by the pre-8C diagnostic-lock paths.
 * Kept because historical rows exist and consumers still read them; NOT the
 * shape any current code writes. `locked_minutes` and `diagnostic_minutes` are
 * browser-computed timings that no longer exist — see
 * {@link DiagnosticCapturedEventMetadataV2}.
 */
export interface DiagnosticCapturedEventMetadataV1 {
  locked_minutes: number;
  outcome: DiagnosticOutcomeExtended;
  confidence: DiagnosticConfidence;
  complaint_confirmed: boolean;
  pause_reason: string;
  job_name: string;
  lock_type: DiagnosticLockType;
  technician_profile_id?: string | null;
  category_key?: string;
  diagnostic_minutes?: number;
}

/**
 * Pensieve `diagnostic.captured` metadata — TIMER V2 (capture_schema_version 2).
 *
 * Every field except the three human assertions is derived by the server from
 * canonical rows. There is deliberately NO timing number: at capture time the
 * allocation is unreviewed, and emitting `0` would be read later as a measured
 * duration rather than as "not known yet".
 */
export interface DiagnosticCapturedEventMetadataV2 {
  capture_schema_version: 2;
  /** Server classification. The client may display it; it may not assert it. */
  capture_kind: DiagnosticLockType;
  job_name: string;
  job_category: string | null;
  /** Learning category, preserved from the pre-8C contract. */
  category_key: string;
  technician_profile_id: string | null;
  work_episode_id: string | null;
  /** ── the only human assertions ─────────────────────────────────────── */
  outcome: DiagnosticOutcomeExtended;
  /** CORE-I: what the diagnostic work demonstrated (required by the capture contract, read by the aggregator). */
  competency_outcome: DiagnosticOutcome;
  confidence: DiagnosticConfidence;
  complaint_confirmed: boolean;
  /** Where time comes from — and that it is not this event. */
  time_authority: 'TIMER_V2';
  allocation_state: 'UNREVIEWED';
}

/**
 * Either shape, for consumers that read historical and current events.
 * Narrow on `capture_schema_version` before touching any timing field.
 */
export type DiagnosticCapturedEventMetadata =
  | DiagnosticCapturedEventMetadataV1
  | DiagnosticCapturedEventMetadataV2;

/**
 * Outcome display labels for UI
 */
export const DIAGNOSTIC_OUTCOME_LABELS: Record<DiagnosticOutcomeExtended, string> = {
  fixed: 'Fixed',
  needs_authorization: 'Needs Authorization',
  needs_parts: 'Needs Parts',
  referred_out: 'Referred Out',
  could_not_duplicate: 'Could Not Duplicate',
};

/**
 * Competency outcome labels. CORE-I's own vocabulary, deliberately NOT the
 * disposition list above.
 */
export const DIAGNOSTIC_COMPETENCY_LABELS: Record<DiagnosticOutcome, string> = {
  confirmed: 'Confirmed — found the fault',
  partial: 'Partial — narrowed it down',
  misdiagnosis: 'Misdiagnosis — first conclusion was wrong',
  could_not_duplicate: 'Could not duplicate — fault did not present',
};

/**
 * Confidence level display labels for UI
 */
export const CONFIDENCE_LABELS: Record<DiagnosticConfidence, string> = {
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Very High',
};
